import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

const FIELD_CONFIG = {
  beneficiary: {
    label: 'Beneficiary',
    color: '#2563eb',
    bgColor: 'rgba(37, 99, 235, 0.15)',
    fields: { left: 'beneficiaryLeft', top: 'beneficiaryTop', width: 'beneficiaryWidth' },
    defaultWidthCm: 8,
  },
  beneficiary2: {
    label: 'Beneficiary L2',
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.12)',
    fields: { left: 'beneficiary2Left', top: 'beneficiary2Top' },
    defaultWidthCm: 8,
  },
  date: {
    label: 'Date',
    color: '#16a34a',
    bgColor: 'rgba(22, 163, 74, 0.15)',
    fields: { left: 'dateLeft', top: 'dateTop', width: 'dateWidth' },
    defaultWidthCm: 4,
  },
  amount: {
    label: 'Amount',
    color: '#ea580c',
    bgColor: 'rgba(234, 88, 12, 0.15)',
    fields: { left: 'amountLeft', top: 'amountTop', width: 'amountWidth' },
    defaultWidthCm: 4,
  },
  amountInWords1: {
    label: 'Words L1',
    color: '#7c3aed',
    bgColor: 'rgba(124, 58, 237, 0.15)',
    fields: { left: 'amountInWords1Left', top: 'amountInWords1Top', width: 'amountInWordsWidth' },
    defaultWidthCm: 8,
  },
  amountInWords2: {
    label: 'Words L2',
    color: '#9333ea',
    bgColor: 'rgba(147, 51, 234, 0.12)',
    fields: { left: 'amountInWords2Left', top: 'amountInWords2Top', width: 'amount2Width' },
    defaultWidthCm: 8,
  },
  accountPayee: {
    label: 'A/C Payee',
    color: '#0891b2',
    bgColor: 'rgba(8, 145, 178, 0.15)',
    fields: { left: 'accountPayeeLeft', top: 'accountPayeeTop' },
    defaultWidthCm: 3,
  },
  remarks: {
    label: 'Remarks',
    color: '#64748b',
    bgColor: 'rgba(100, 116, 139, 0.15)',
    fields: { left: 'remarksLeft', top: 'remarksTop', width: 'remarksWidth' },
    defaultWidthCm: 5,
  }
};

const DEFAULT_FIELD_HEIGHT_CM = 0.5;
const DEFAULT_FIELD_WIDTH_CM = 3;

const ChequeImagePreview = ({
  imageUrl, templateWidth, templateHeight, formValues,
  activeField, onFieldClick, onFieldMove, onFieldResize,
  placementField, onPlaceField, hiddenFields = [],
  designerEnabled = false,
}) => {
  const containerRef = useRef(null);
  const [dragState, setDragState] = useState(null);

  const tw = parseFloat(templateWidth);
  const th = parseFloat(templateHeight);
  const validDimensions = !isNaN(tw) && !isNaN(th) && tw > 0 && th > 0;

  const overlays = useMemo(() => {
    if (!validDimensions) return [];

    return Object.entries(FIELD_CONFIG).filter(([key]) => !hiddenFields.includes(key)).map(([key, config]) => {
      const leftVal = parseFloat(formValues?.[config.fields.left]);
      const topVal = parseFloat(formValues?.[config.fields.top]);

      if (isNaN(leftVal) || isNaN(topVal)) return null;

      const defaultWidth = config.defaultWidthCm || DEFAULT_FIELD_WIDTH_CM;
      const widthVal = config.fields.width ? parseFloat(formValues?.[config.fields.width]) : defaultWidth;
      const fieldWidth = isNaN(widthVal) || widthVal <= 0 ? defaultWidth : widthVal;

      const leftPct = (leftVal / tw) * 100;
      const topPct = ((topVal - DEFAULT_FIELD_HEIGHT_CM) / th) * 100;
      const widthPct = (fieldWidth / tw) * 100;
      const heightPct = (DEFAULT_FIELD_HEIGHT_CM / th) * 100;

      if (leftPct < -10 || leftPct > 110 || topPct < -10 || topPct > 110) return null;

      return {
        key,
        label: config.label,
        color: config.color,
        bgColor: config.bgColor,
        leftPct: Math.max(0, Math.min(leftPct, 100)),
        topPct: Math.max(0, Math.min(topPct, 100)),
        widthPct: Math.min(widthPct, 100 - Math.max(0, leftPct)),
        heightPct: Math.min(heightPct, 100 - Math.max(0, topPct)),
        hasWidth: !!config.fields.width,
      };
    }).filter(Boolean);
  }, [validDimensions, tw, th, formValues, hiddenFields]);

  const handleMoveStart = useCallback((e, fieldKey) => {
    if (!designerEnabled) return;
    // Skip drag when placement is active
    if (placementField) return;
    if (!validDimensions || !containerRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const config = FIELD_CONFIG[fieldKey];
    if (!config) return;

    const leftCm = parseFloat(formValues?.[config.fields.left]) || 0;
    const topCm = parseFloat(formValues?.[config.fields.top]) || 0;

    setDragState({
      type: 'move',
      fieldKey,
      startX: e.clientX,
      startY: e.clientY,
      startLeftCm: leftCm,
      startTopCm: topCm,
    });

    document.body.style.cursor = 'grabbing';
  }, [designerEnabled, placementField, validDimensions, formValues]);

  const handleResizeStart = useCallback((e, fieldKey) => {
    if (!designerEnabled) return;
    if (placementField) return;
    if (!validDimensions || !containerRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const config = FIELD_CONFIG[fieldKey];
    if (!config?.fields.width) return;

    const widthCm = parseFloat(formValues?.[config.fields.width]) || config.defaultWidthCm || DEFAULT_FIELD_WIDTH_CM;
    const leftCm = parseFloat(formValues?.[config.fields.left]) || 0;

    setDragState({
      type: 'resize-right',
      fieldKey,
      startX: e.clientX,
      startY: e.clientY,
      startWidthCm: widthCm,
      startLeftCm: leftCm,
    });

    document.body.style.cursor = 'col-resize';
  }, [designerEnabled, placementField, validDimensions, formValues]);

  useEffect(() => {
    if (!dragState) return;

    const handlePointerMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pxPerCmX = rect.width / tw;
      const pxPerCmY = rect.height / th;

      if (dragState.type === 'move') {
        const deltaCmX = (e.clientX - dragState.startX) / pxPerCmX;
        const deltaCmY = (e.clientY - dragState.startY) / pxPerCmY;

        const newLeft = Math.round(Math.max(0, Math.min(tw, dragState.startLeftCm + deltaCmX)) * 100) / 100;
        const newTop = Math.round(Math.max(DEFAULT_FIELD_HEIGHT_CM, Math.min(th, dragState.startTopCm + deltaCmY)) * 100) / 100;

        onFieldMove?.(dragState.fieldKey, newLeft, newTop);
      } else if (dragState.type === 'resize-right') {
        const deltaCmX = (e.clientX - dragState.startX) / pxPerCmX;
        const maxWidth = tw - dragState.startLeftCm;
        const newWidth = Math.round(Math.max(0.5, Math.min(maxWidth, dragState.startWidthCm + deltaCmX)) * 100) / 100;

        onFieldResize?.(dragState.fieldKey, newWidth);
      }
    };

    const handlePointerUp = () => {
      setDragState(null);
      document.body.style.cursor = '';
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = '';
    };
  }, [dragState, tw, th, onFieldMove, onFieldResize]);

  // Convert pixel click position to cm coordinates
  const clickToCm = useCallback((e) => {
    if (!containerRef.current || !validDimensions) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const xPct = (e.clientX - rect.left) / rect.width;
    const yPct = (e.clientY - rect.top) / rect.height;
    const leftCm = Math.round(Math.max(0, Math.min(tw, xPct * tw)) * 100) / 100;
    const topCm = Math.round(Math.max(DEFAULT_FIELD_HEIGHT_CM, Math.min(th, yPct * th + DEFAULT_FIELD_HEIGHT_CM)) * 100) / 100;
    return { leftCm, topCm, xPct, yPct };
  }, [validDimensions, tw, th]);

  const handleContainerClick = useCallback((e) => {
    if (!designerEnabled) return;
    if (!validDimensions) return;

    // Click-to-place mode
    if (placementField) {
      const coords = clickToCm(e);
      if (coords) {
        onPlaceField?.(placementField, coords.leftCm, coords.topCm);
      }
    }
  }, [designerEnabled, validDimensions, placementField, clickToCm, onPlaceField]);

  const containerCursor = !designerEnabled ? 'default' : placementField ? 'crosshair' : 'default';

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#f8f9fa',
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt="Cheque template"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            paddingTop: validDimensions
              ? `${(th / tw) * 100}%`
              : '45%',
            backgroundColor: '#f0f4ff',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#94a3b8',
            }}
          >
            <Typography variant="body2">No image uploaded</Typography>
            <Typography variant="caption">Field positions will be shown as overlays</Typography>
          </Box>
        </Box>
      )}

      {/* Field overlays + interaction layer */}
      <Box
        ref={containerRef}
        onClick={handleContainerClick}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          cursor: containerCursor,
        }}
      >
        {/* Placement banner */}
        {placementField && FIELD_CONFIG[placementField] && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              backgroundColor: `${FIELD_CONFIG[placementField].color}dd`,
              color: 'white',
              textAlign: 'center',
              py: 0.5,
              px: 1,
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            Click to place: {FIELD_CONFIG[placementField].label}
          </Box>
        )}

        {overlays.map((overlay) => {
          const isDragging = dragState?.fieldKey === overlay.key;
          const isAnyDragging = !!dragState;
          const isPlacing = !!placementField;

          return (
            <Tooltip
              key={overlay.key}
              title={dragState ? '' : !designerEnabled ? `${overlay.label} (locked)` : !isPlacing ? `${overlay.label} — drag to move${overlay.hasWidth ? ', drag right edge to resize' : ''}` : ''}
              arrow
              placement="top"
              disableHoverListener={!!dragState || isPlacing}
            >
              <Box
                onPointerDown={(e) => handleMoveStart(e, overlay.key)}
                onClick={(e) => {
                  if (!designerEnabled) return;
                  if (isPlacing) {
                    // Let click pass through to container for placement
                    return;
                  }
                  e.stopPropagation();
                  if (!dragState) onFieldClick?.(overlay.key);
                }}
                sx={{
                  position: 'absolute',
                  left: `${overlay.leftPct}%`,
                  top: `${overlay.topPct}%`,
                  width: `${overlay.widthPct}%`,
                  height: `${overlay.heightPct}%`,
                  minHeight: 16,
                  minWidth: 24,
                  backgroundColor: overlay.bgColor,
                  border: `2px solid ${overlay.color}`,
                  borderRadius: 0.5,
                  cursor: !designerEnabled ? 'default' : isPlacing ? 'crosshair' : isDragging ? 'grabbing' : 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: isDragging ? 'none' : 'opacity 0.15s ease, box-shadow 0.15s ease',
                  opacity: isAnyDragging
                    ? (isDragging ? 1 : 0.3)
                    : (activeField && activeField !== overlay.key ? 0.4 : 1),
                  boxShadow: isDragging
                    ? `0 0 0 2px ${overlay.color}, 0 4px 16px rgba(0,0,0,0.25)`
                    : activeField === overlay.key
                      ? `0 0 0 2px ${overlay.color}, 0 2px 8px rgba(0,0,0,0.15)`
                      : 'none',
                  zIndex: isDragging ? 20 : activeField === overlay.key ? 10 : 1,
                  userSelect: 'none',
                  touchAction: 'none',
                  '&:hover': isAnyDragging ? {} : {
                    opacity: 1,
                    boxShadow: `0 0 0 2px ${overlay.color}, 0 2px 8px rgba(0,0,0,0.15)`,
                    zIndex: 10,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    color: overlay.color,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    px: 0.25,
                    pointerEvents: 'none',
                  }}
                >
                  {overlay.label}
                </Typography>

                {/* Right-edge resize handle (only for fields with width) */}
                {overlay.hasWidth && !isPlacing && designerEnabled && (
                  <Box
                    onPointerDown={(e) => handleResizeStart(e, overlay.key)}
                    sx={{
                      position: 'absolute',
                      right: -3,
                      top: 0,
                      bottom: 0,
                      width: 8,
                      cursor: 'col-resize',
                      zIndex: 2,
                      '&:hover': {
                        backgroundColor: `${overlay.color}40`,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3,
                        height: 12,
                        borderLeft: `2px dotted ${overlay.color}`,
                      },
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export { FIELD_CONFIG };
export default React.memo(ChequeImagePreview);
